import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Envelope, MapPin, CheckCircle } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
    const { theme } = useTheme();
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        type_personne: 'physique',
        nom: '',
        prenom: '',
        ninea_rc_siret: '',
        telephone: '',
        email: '',
        raison: '',
        message: ''
    });

    const handleInputChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/messages`, formData);
            setSuccess(true);
            setFormData({
                type_personne: 'physique',
                nom: '',
                prenom: '',
                ninea_rc_siret: '',
                telephone: '',
                email: '',
                raison: '',
                message: ''
            });
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Erreur lors de l\'envoi du message');
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div data-testid="contact-success" className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center p-8"
                >
                    <CheckCircle size={80} className="mx-auto mb-6 text-green-500" weight="fill" />
                    <h2 className="font-heading text-2xl sm:text-3xl font-medium mb-4">Message envoyé !</h2>
                    <p className={`mb-8 ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                        Nous avons bien reçu votre message et vous répondrons dans les plus brefs délais.
                    </p>
                    <Button onClick={() => setSuccess(false)} data-testid="send-another-message">
                        Envoyer un autre message
                    </Button>
                </motion.div>
            </div>
        );
    }

    return (
        <div data-testid="contact-page" className="min-h-screen">
            {/* Hero */}
            <section className={`py-16 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-mmd-light-bg'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center"
                    >
                        <p className={`text-sm font-medium tracking-[0.2em] uppercase mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                            Contact
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tighter mb-4">
                            Contactez-nous
                        </h1>
                        <p className={`max-w-xl mx-auto ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                            Une question, un projet ? N'hésitez pas à nous contacter.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Form & Info */}
            <section className={`py-12 ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Contact Info */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="lg:col-span-1"
                        >
                            <h2 className="font-heading text-2xl font-medium mb-6">Nos coordonnées</h2>
                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-mmd-gold/20' : 'bg-mmd-navy/10'}`}>
                                        <Phone size={24} className={theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'} />
                                    </div>
                                    <div>
                                        <h3 className="font-medium mb-1">Téléphone</h3>
                                        <a href="tel:+221768462040" className="text-muted-foreground hover:text-mmd-gold transition-colors">
                                            +221 76 846 20 40
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-mmd-gold/20' : 'bg-mmd-navy/10'}`}>
                                        <Envelope size={24} className={theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'} />
                                    </div>
                                    <div>
                                        <h3 className="font-medium mb-1">Email</h3>
                                        <a href="mailto:contact@mmd-global.com" className="text-muted-foreground hover:text-mmd-gold transition-colors">
                                            contact@mmd-global.com
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-mmd-gold/20' : 'bg-mmd-navy/10'}`}>
                                        <MapPin size={24} className={theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'} />
                                    </div>
                                    <div>
                                        <h3 className="font-medium mb-1">Adresse</h3>
                                        <p className="text-muted-foreground">Dakar, Sénégal</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="lg:col-span-2"
                        >
                            <div className={`p-6 sm:p-8 rounded-2xl ${theme === 'dark' ? 'bg-mmd-surface-dark border border-white/10' : 'bg-mmd-light-bg border border-black/5'}`}>
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Type de personne */}
                                    <div>
                                        <Label className="mb-3 block">Type de demandeur</Label>
                                        <RadioGroup
                                            value={formData.type_personne}
                                            onValueChange={(value) => handleSelectChange('type_personne', value)}
                                            className="flex gap-6"
                                            data-testid="type-personne"
                                        >
                                            <div className="flex items-center gap-2">
                                                <RadioGroupItem value="physique" id="physique" />
                                                <Label htmlFor="physique" className="cursor-pointer">Personne physique</Label>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <RadioGroupItem value="morale" id="morale" />
                                                <Label htmlFor="morale" className="cursor-pointer">Personne morale</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    {/* Nom / Prénom ou NINEA */}
                                    {formData.type_personne === 'physique' ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="nom">Nom</Label>
                                                <Input
                                                    id="nom"
                                                    name="nom"
                                                    value={formData.nom}
                                                    onChange={handleInputChange}
                                                    required
                                                    data-testid="contact-nom"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="prenom">Prénom</Label>
                                                <Input
                                                    id="prenom"
                                                    name="prenom"
                                                    value={formData.prenom}
                                                    onChange={handleInputChange}
                                                    required
                                                    data-testid="contact-prenom"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <Label htmlFor="ninea_rc_siret">NINEA / RC / SIRET</Label>
                                            <Input
                                                id="ninea_rc_siret"
                                                name="ninea_rc_siret"
                                                value={formData.ninea_rc_siret}
                                                onChange={handleInputChange}
                                                required
                                                data-testid="contact-ninea"
                                            />
                                        </div>
                                    )}

                                    {/* Téléphone & Email */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="telephone">Téléphone</Label>
                                            <Input
                                                id="telephone"
                                                name="telephone"
                                                type="tel"
                                                value={formData.telephone}
                                                onChange={handleInputChange}
                                                required
                                                data-testid="contact-telephone"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="email">Email</Label>
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                data-testid="contact-email"
                                            />
                                        </div>
                                    </div>

                                    {/* Raison */}
                                    <div>
                                        <Label htmlFor="raison">Raison du contact</Label>
                                        <Select value={formData.raison} onValueChange={(value) => handleSelectChange('raison', value)}>
                                            <SelectTrigger data-testid="contact-raison">
                                                <SelectValue placeholder="Sélectionnez une raison" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="devis">Demande de devis</SelectItem>
                                                <SelectItem value="produit">Demande de produit</SelectItem>
                                                <SelectItem value="partenariat">Partenariat</SelectItem>
                                                <SelectItem value="autre">Autre</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <Label htmlFor="message">Message</Label>
                                        <Textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            rows={5}
                                            required
                                            data-testid="contact-message"
                                        />
                                    </div>

                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="contact-submit"
                                        className={`w-full py-6 text-lg font-medium ${
                                            theme === 'dark'
                                                ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover'
                                                : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'
                                        }`}
                                    >
                                        {submitting ? 'Envoi en cours...' : 'Envoyer le message'}
                                    </Button>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
}
